import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { DynamicDialogRef, DynamicDialogConfig } from "primeng/dynamicdialog";
import { Observable, Subscription } from "rxjs";
import { AppState } from "src/app/store/app.reducer";
import { selectLoginMfaCode } from "src/app/store/auth/auth.selector";
import * as AuthActions from 'src/app/store/auth/auth.actions';
import { StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { Lang } from "src/app/model/lang";
import { selectCurrentLang } from "src/app/store/user/user.selector";
import { DEFAULT_LANG } from "src/app/conf/languages";
import { FormControl } from "@angular/forms";

@Component({
	templateUrl: './login-code-mfa.dialog.component.html',
	styles: [
		`
		.custom-otp-input {
			width: 45px;
			font-family: 'Roboto';
			font-weight: 400;
			font-size: 36px;
			border: 0 none;
			appearance: none;
			text-align: center;
			transition: all 0.2s;
			background: transparent;
			border-bottom: 2px solid var(--blue-400);
			border-radius: 0px;
			margin: 0 0.2rem;
		}

		.custom-otp-input:focus {
			outline: 0 none;
			box-shadow: none;
			border-bottom-color: var(--primary-color);
		}

		.custom-mfa-input {
			width: 170px;
			font-family: 'Roboto';
			font-weight: 400;
			font-size: 36px;
			border: 0 none;
			appearance: none;
			text-align: center;
			transition: all 0.2s;
			background: transparent;
			border-bottom: 2px solid var(--blue-400);
			border-radius: 0px;
			margin: 0 0.2rem;
		}

		.custom-mfa-input:focus {
			outline: 0 none;
			box-shadow: none;
			border-bottom-color: var(--primary-color);
		}

		img {
			user-select: none;
			-webkit-user-drag: none;
			user-drag: none;
		}
		`
	],
})

export class LoginCodeMfaDialogComponent implements OnInit, OnDestroy {
	loginMfaCode$: Observable<number | undefined>;
	loginMfaCodeSub: Subscription | undefined;
	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	pinLength = 6;
	display: string[] = ['', '', '', '', '', ''];

	pin: number[] = [];
	pinStr: string = '';

	pinpadBtns: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

	showPinPad: boolean;
	resendEnable: boolean;

	visibleMode = false;
	username: string;
	password: string;
	pinValue: FormControl = new FormControl('');

	// design
	screenWidth: number;
	MAX_MD_WIDTH = 1120;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		if (config.data !== undefined) {
			this.username = config.data.username;
			this.password = config.data.password;
		}
		this.initSelectors();
	}

	ngOnInit(): void {
		this.resendEnable = false;
		this.subscribeAll();
		this.onResize();
		setTimeout(() => {
			this.resendEnable = true;
		}, 30000);
	}

	initSelectors() {
		this.loginMfaCode$ = this.store.select(selectLoginMfaCode);
		this.currentLang$ = this.store.select(selectCurrentLang);
	}

	subscribeAll() {
		this.loginMfaCodeSub = this.loginMfaCode$.subscribe(loginMfaCode => {
			if (loginMfaCode === 4) { // Close DIALOG
				this.store.dispatch(AuthActions.authMfaCodeReset());
				this.closeDialog();
			}
		});
		this.currentLang$.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
	}

	ngOnDestroy(): void {
		this.loginMfaCodeSub?.unsubscribe();
	}

	closeDialog(): void {
		this.ref.close();
	}

	redrawDisplay() {
		const newDisplay = ['', '', '', '', '', ''];
		for (let i = 0; i < this.pinLength; i++) {
			this.drawDigit(newDisplay, this.pin[i], i);
		}
		this.display = newDisplay;
	}

	drawDigit(display: string[], digit: number, position: number): void {
		if (digit === undefined) {
			return;
		}
		if (this.visibleMode) {
			display[position] = digit.toString();
		} else {
			display[position] = '\u2022';
		}
	}

	onKeyPressed(num: number) {
		if (this.pinValue.value.length === this.pinLength) {
			return;
		}
		if (this.pin.length === this.pinLength) {
			return;
		}
		this.pin = this.pin.concat(num);
		this.pinStr = this.pinStr + num;
		this.pinValue.setValue(this.pinValue.value + num);
		this.redrawDisplay();
	}

	onClearPressed() {
		this.pinValue.setValue('');
		this.pin = [];
		this.pinStr = '';
		this.redrawDisplay();
	}

	onOkPressed() {
		this.sendCodeAuth();
		this.pinValue.setValue('');
		this.pin = [];
		this.pinStr = '';
		this.redrawDisplay();
	}

	toggleVisibility(): void {
		this.visibleMode = !this.visibleMode;
		this.redrawDisplay();
	}

	resendPin(): void {
		this.resendEnable = false;
		this.store.dispatch(AuthActions.authMfaCode({
			username: this.username,
			password: this.password,
			mfaCode: '',
		}));
		setTimeout(() => {
			this.resendEnable = true;
		}, 30000);
	}


	sendCodeAuth() {
		this.store.dispatch(AuthActions.authMfaCode({
			username: this.username,
			password: this.password,
			mfaCode: this.pinValue.value,
		}));
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
